'use client';

import {
  Field,
  WizardFormSection,
  WizardPanelBody,
  WizardPanelCard,
  WizardPanelHeader,
  inputClass,
} from '@/components/event-wizard/wizard-ui';
import { WizardRichTextField } from '@/components/event-wizard/WizardRichTextField';
import { ToggleRow } from '@/components/event-wizard/wizard-form-controls';
import {
  MANDATORY_RULE_FIELDS,
  type RulesFormState,
} from '@/components/event-wizard/wizard-utils';
import type { AdvancedSettingsFormState } from '@/components/event-wizard/advanced-settings-utils';
import { ImageFileUpload } from '@/components/organization/ImageFileUpload';
import { resolveStorageUrl } from '@/lib/storage-url';
import { validateEventCoverImage, validateOrgBanner, validateOrgLogo } from '@/lib/upload-validation';

type MediaState = {
  cover_image: string;
  logo: string;
  banner: string;
};

type MediaUploadProps = {
  previewUrl: string | null;
  uploadProgress: number | null;
  uploadError: string | null;
  uploadKey: number;
  disabled?: boolean;
  onFileSelect: (file: File | null) => void;
};

export function AdvancedSettingsPanel({
  form,
  onChange,
  rulesForm,
  onRulesChange,
  media,
  onDirty,
  coverUpload,
  logoUpload,
  bannerUpload,
}: {
  form: AdvancedSettingsFormState;
  onChange: (next: AdvancedSettingsFormState) => void;
  rulesForm: RulesFormState;
  onRulesChange: (next: RulesFormState) => void;
  media: MediaState;
  onDirty?: () => void;
  coverUpload: MediaUploadProps;
  logoUpload: MediaUploadProps;
  bannerUpload: MediaUploadProps;
  /** @deprecated Evaluation/discovery live in event workspace. Kept for call-site compatibility. */
  eventStatus?: string;
  /** @deprecated Evaluation/discovery live in event workspace. Kept for call-site compatibility. */
  eventId?: string;
}) {
  const patchAttendance = (partial: Partial<AdvancedSettingsFormState['attendance']>) => {
    onDirty?.();
    onChange({ ...form, attendance: { ...form.attendance, ...partial } });
  };

  const patchNotifications = (partial: Partial<AdvancedSettingsFormState['notifications']>) => {
    onDirty?.();
    onChange({ ...form, notifications: { ...form.notifications, ...partial } });
  };

  const patchBranding = (partial: Partial<AdvancedSettingsFormState['branding']>) => {
    onDirty?.();
    onChange({ ...form, branding: { ...form.branding, ...partial } });
  };

  return (
    <WizardPanelCard>
      <WizardPanelHeader
        embedded
        title="Advanced settings"
        description="Policies, attendance, communications, and branding. Evaluation and discovery are configured in the event workspace after publish."
      />

      <WizardPanelBody>
        <WizardFormSection title="Rules & policies" description="Optional guidance shown to participants before they register.">
          <div className="grid gap-3 sm:grid-cols-2">
            {MANDATORY_RULE_FIELDS.map(({ key, label }) => (
              <Field key={key} label={label}>
                <WizardRichTextField
                  value={rulesForm[key]}
                  placeholder={`Enter ${label.toLowerCase()}…`}
                  minHeightClass="min-h-[88px]"
                  compact
                  onChange={(html) => {
                    onDirty?.();
                    onRulesChange({ ...rulesForm, [key]: html });
                  }}
                />
              </Field>
            ))}
          </div>
        </WizardFormSection>

        <WizardFormSection
          title="Attendance settings"
          description="Configure check-in behavior and session rules for on-site or hybrid events."
          divider
        >
          <div className="grid gap-2 sm:grid-cols-2">
            <ToggleRow
              compact
              label="Attendance enabled"
              hint="Turn on attendance tracking for this event."
              checked={form.attendance.enabled}
              onChange={(v) => patchAttendance({ enabled: v })}
            />
            <ToggleRow
              compact
              label="Multiple sessions per day"
              hint="Allow separate sessions within the same day."
              checked={form.attendance.multiple_sessions_per_day}
              onChange={(v) => patchAttendance({ multiple_sessions_per_day: v })}
            />
            <ToggleRow
              compact
              label="Allow late scans"
              hint="Accept check-ins after the session window ends."
              checked={form.attendance.allow_late_scans}
              onChange={(v) => patchAttendance({ allow_late_scans: v })}
            />
            <ToggleRow
              compact
              label="Manual attendance allowed"
              hint="Organizers can mark attendance from the dashboard."
              checked={form.attendance.manual_attendance_allowed}
              onChange={(v) => patchAttendance({ manual_attendance_allowed: v })}
            />
          </div>
        </WizardFormSection>

        <WizardFormSection
          title="Communication settings"
          description="Choose which automated updates attendees and organizers receive."
          divider
        >
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            <ToggleRow
              compact
              label="Email updates"
              hint="Confirmations, reminders, and event updates."
              checked={form.notifications.email_updates}
              onChange={(v) => patchNotifications({ email_updates: v })}
            />
            <ToggleRow
              compact
              label="Campaign reminders"
              hint="Remind attendees about sessions or deadlines."
              checked={form.notifications.campaign_reminders}
              onChange={(v) => patchNotifications({ campaign_reminders: v })}
            />
            <ToggleRow
              compact
              label="Announcement notifications"
              hint="Push organizer announcements to participants."
              checked={form.notifications.announcement_notifications}
              onChange={(v) => patchNotifications({ announcement_notifications: v })}
            />
          </div>
        </WizardFormSection>

        <WizardFormSection
          title="Branding"
          description="Visual identity for your event page, emails, and listings."
          divider
        >
          <Field label="Event accent color" hint="Hex color for buttons and highlights.">
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="color"
                className="h-9 w-11 cursor-pointer rounded-lg border border-nature-border bg-bg p-0.5"
                value={form.branding.accent_color || '#697C70'}
                onChange={(e) => patchBranding({ accent_color: e.target.value })}
              />
              <input
                className={`${inputClass} max-w-[120px] !py-1.5 !text-xs`}
                value={form.branding.accent_color}
                onChange={(e) => patchBranding({ accent_color: e.target.value })}
                placeholder="#697C70"
              />
            </div>
          </Field>
          <div className="space-y-4">
            <div data-field="cover_image">
              <ImageFileUpload
                key={coverUpload.uploadKey}
                label="Cover Image"
                hint="Optional hero image for listings and the event page. JPG, PNG, or WEBP."
                accept="image/jpeg,image/jpg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                currentUrl={coverUpload.previewUrl ?? resolveStorageUrl(media.cover_image)}
                onFileSelect={coverUpload.onFileSelect}
                validate={validateEventCoverImage}
                uploadProgress={coverUpload.uploadProgress}
                disabled={coverUpload.disabled}
                frameClass="h-[100px] w-full"
                objectFit="contain"
              />
              {coverUpload.uploadError && (
                <p className="mt-1 text-xs text-soot">{coverUpload.uploadError}</p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-[148px_minmax(0,1fr)] lg:items-start">
              <div className="min-w-0" data-field="logo">
                <ImageFileUpload
                  key={logoUpload.uploadKey}
                  label="Event logo"
                  hint="Square mark for headers and tickets."
                  accept="image/jpeg,image/jpg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                  currentUrl={logoUpload.previewUrl ?? resolveStorageUrl(media.logo)}
                  onFileSelect={logoUpload.onFileSelect}
                  validate={validateOrgLogo}
                  uploadProgress={logoUpload.uploadProgress}
                  disabled={logoUpload.disabled}
                  frameClass="h-[136px] w-[136px]"
                  objectFit="contain"
                />
                {logoUpload.uploadError && (
                  <p className="mt-1 text-xs text-soot">{logoUpload.uploadError}</p>
                )}
              </div>
              <div className="min-w-0" data-field="banner">
                <ImageFileUpload
                  key={bannerUpload.uploadKey}
                  label="Banner image"
                  hint="Wide banner for the event page header."
                  accept="image/jpeg,image/jpg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                  currentUrl={bannerUpload.previewUrl ?? resolveStorageUrl(media.banner)}
                  onFileSelect={bannerUpload.onFileSelect}
                  validate={validateOrgBanner}
                  uploadProgress={bannerUpload.uploadProgress}
                  disabled={bannerUpload.disabled}
                  frameClass="h-[100px] w-full"
                  objectFit="contain"
                />
                {bannerUpload.uploadError && (
                  <p className="mt-1 text-xs text-soot">{bannerUpload.uploadError}</p>
                )}
              </div>
            </div>
          </div>
        </WizardFormSection>
      </WizardPanelBody>
    </WizardPanelCard>
  );
}
